import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Typography, Space, Descriptions, Button, Input, Divider, Select, Tag, List } from "antd";
import dayjs from "dayjs";
import { useShow, useList, useCreate, useUpdate } from "@refinedev/core";
import { useNotify } from "../../hooks/useNotify";
import { RESPONSE_STATUSES } from "@/utils/formatters";

export default function ReviewShow() {
  const notify = useNotify();
  const { id } = useParams();
  const [finalText, setFinalText] = useState<string>("");
  const [style, setStyle] = useState<string | undefined>(undefined);
  const [topics, setTopics] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(undefined);

  // Fetch review data
  const reviewQuery = useShow({
    resource: "reviews",
    id: id || "",
  });

  const r = reviewQuery.query?.data?.data?.review;

  // Fetch responses for this review
  const responsesQuery = useList({
    resource: "responses",
    filters: [{ field: "review_id", operator: "eq", value: id }],
  });

  const responses = responsesQuery.result?.data || [];
  const latest = responses[0];
  const refetchResponses = responsesQuery.query.refetch;

  // Fetch templates
  const templatesQuery = useList({
    resource: "templates",
    filters: [{ field: "is_active", operator: "eq", value: true }],
  });

  const templates = templatesQuery.result?.data || [];

  // Create response mutation
  const { mutate: createResponse } = useCreate();

  // Update response mutation
  const { mutate: updateResponse } = useUpdate();

  useEffect(() => {
    if (latest) {
      setFinalText(latest.final_text || latest.generated_text || "");
    }
  }, [latest]);

  // Функция для перевода статусов ответов
  const getResponseStatusLabel = (status: string) => {
    return RESPONSE_STATUSES[status]?.label || status;
  };

  if (reviewQuery.query?.isLoading) return <div>Loading...</div>;
  if (!r) return <div>Review not found</div>;

  const handleGenerate = () => {
    // Подготовка параметров для генерации
    const payload: any = {
      review_id: r.id,
      style: style || undefined,
      topics: topics.length > 0 ? topics : undefined,
      template_id: selectedTemplate || undefined,
      use_rag: true, // Всегда используем RAG для качественных ответов
    };

    createResponse(
      {
        resource: "responses",
        values: payload,
      },
      {
        onSuccess: () => {
          notify.success("Ответ успешно сгенерирован");
          refetchResponses();
        },
        onError: (error: any) => {
          notify.error("Ошибка генерации", error.message || String(error));
        },
      }
    );
  };

  const handleSaveDraft = async () => {
    if (!latest) {
      notify.warning("Нет черновика ответа");
      return;
    }

    try {
      // PATCH обновляет текст и автоматически меняет статус pending → draft
      const response = await fetch(`http://localhost:8085/api/v1/responses/${latest.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ final_text: finalText })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      notify.success("Черновик сохранен");
      setTimeout(() => {
        refetchResponses();
        reviewQuery.query.refetch();
      }, 500);
    } catch (error: any) {
      notify.error("Ошибка сохранения", error.message || String(error));
    }
  };

  const handleApprove = async () => {
    if (!latest) return;
    try {
      const response = await fetch(`http://localhost:8085/api/v1/responses/${latest.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        notify.success("Ответ утвержден");
        // Принудительно обновляем данные
        setTimeout(() => {
          refetchResponses();
          reviewQuery.query.refetch();
        }, 500);
      } else {
        const errorText = await response.text();
        notify.error("Ошибка утверждения", errorText);
      }
    } catch (error: any) {
      notify.error("Ошибка", error.message || String(error));
    }
  };

  const handlePublish = () => {
    if (!latest) return;
    updateResponse(
      {
        resource: "responses",
        id: latest.id,
        values: { status: "published", final_text: finalText },
      },
      {
        onSuccess: () => {
          notify.success("Опубликовано");
          refetchResponses();
        },
        onError: (error: any) => {
          notify.error("Ошибка", error.message || String(error));
        },
      }
    );
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t: any) => t.id === templateId);
    if (template) {
      // Replace variables with actual values
      let text = template.content;
      text = text.replace(/\{customer_name\}/g, r?.customer_name || "[Имя клиента]");
      text = text.replace(/\{product_name\}/g, r?.product_name || "[Название товара]");
      text = text.replace(/\{resolution_text\}/g, "[Текст решения проблемы]");
      text = text.replace(/\{additional_info\}/g, "[Дополнительная информация]");
      text = text.replace(/\{questions\}/g, "[Вопросы к клиенту]");

      setFinalText(text);
      notify.success(`Шаблон "${template.name}" применен`);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        loading={reviewQuery.query?.isLoading}
        title={<Space>
          <Typography.Text strong>{r?.type === 'question' ? 'Вопрос покупателя' : 'Отзыв'}</Typography.Text>
          {r?.type === 'question' && <Tag color="blue">Q&A</Tag>}
          {r?.has_stop_words ? <Tag color="red">SW</Tag> : null}
        </Space>}
      >
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="Дата">{r.created_at ? dayjs(r.created_at).format("YYYY-MM-DD HH:mm") : ""}</Descriptions.Item>
          <Descriptions.Item label="Источник">{r.source}</Descriptions.Item>
          <Descriptions.Item label="Статус">{r.status}</Descriptions.Item>
          <Descriptions.Item label="Тип">
            {r.type === 'question' ? <Tag color="blue">Вопрос</Tag> : <Tag color="default">Отзыв</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="SKU">{r.product_sku}</Descriptions.Item>
          <Descriptions.Item label="Товар">{r.product_name}</Descriptions.Item>
          <Descriptions.Item label="Рейтинг">
            {r.type === 'question' ? <Typography.Text type="secondary">-</Typography.Text> : (r.rating ?? "-")}
          </Descriptions.Item>
          <Descriptions.Item label="Бренд" span={2}>{r.brand ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Текст" span={3}>{r.text || r.advantages || r.disadvantages || "(нет текста)"}</Descriptions.Item>
          {r.routing_rule && (
            <Descriptions.Item label="Маршрут" span={3}>
              <Space>
                <Typography.Text strong>{r.routing_rule.rule_name}</Typography.Text>
                <Tag color={r.routing_rule.mode === 'template' ? 'blue' : r.routing_rule.mode === 'ai' ? 'green' : 'orange'}>
                  {r.routing_rule.mode}
                </Tag>
                {r.routing_rule.mode === 'manual' && (
                  <Tag color="red">Требуется ручная модерация</Tag>
                )}
              </Space>
            </Descriptions.Item>
          )}
          {r.sentiment && <Descriptions.Item label="Тональность">{r.sentiment}</Descriptions.Item>}
          {r.topics && r.topics.length > 0 && (
            <Descriptions.Item label="Темы" span={2}>
              <Space wrap>{r.topics.map((t: string) => <Tag key={t}>{t}</Tag>)}</Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card
        title={<Typography.Text strong>Настройки генерации</Typography.Text>}
        extra={<Button onClick={handleGenerate}>Сгенерировать ответ</Button>}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space wrap>
            <Select
              allowClear
              placeholder="Шаблон ответа"
              style={{ width: 300 }}
              value={selectedTemplate}
              onChange={handleTemplateSelect}
              options={templates.map((t: any) => ({
                value: t.id,
                label: t.name,
              }))}
            />
            <Select
              allowClear
              placeholder="Стиль ответа"
              style={{ width: 220 }}
              value={style}
              onChange={setStyle}
              options={["профессиональный","заботливый","дружелюбный"].map(v=>({value:v,label:v}))}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Выберите темы"
              style={{ width: 320 }}
              value={topics}
              onChange={setTopics}
              options={["качество_товара", "цена", "доставка", "упаковка", "применение", "состав"].map(v => ({ value: v, label: v }))}
            />
          </Space>
          {r.topics && r.topics.length > 0 && (
            <div>
              <Typography.Text type="secondary">Определенные темы: </Typography.Text>
              <Space wrap>{r.topics.map((t: string) => <Tag key={t} color="blue">{t}</Tag>)}</Space>
            </div>
          )}
        </Space>
      </Card>

      {latest ? (
        <Card
          title={<Typography.Text strong>Ответ</Typography.Text>}
          extra={<Space>
            <Tag color={latest.status === 'draft' ? 'orange' : latest.status === 'approved' ? 'cyan' : latest.status === 'pending' ? 'blue' : 'green'}>
              {getResponseStatusLabel(latest.status)}
            </Tag>
            <Button type="primary" onClick={handleSaveDraft}>Сохранить черновик</Button>
            <Button onClick={handleApprove}>Утвердить</Button>
            <Button onClick={handlePublish}>Опубликовать</Button>
          </Space>}
        >
          <Input.TextArea rows={10} value={finalText} onChange={(e)=>setFinalText(e.target.value)} placeholder="Текст ответа" />
          {!!latest?.rag_sources && Array.isArray(latest.rag_sources) && latest.rag_sources.length > 0 && (
            <>
              <Divider />
              <Typography.Text strong>Источники (RAG)</Typography.Text>
              <List
                size="small"
                dataSource={latest.rag_sources}
                renderItem={(it: any) => (
                  <List.Item>
                    <Space>
                      <Tag>{it.title || it.doc_id || it.metadata?.title}</Tag>
                      <Typography.Text type="secondary">score: {it.score ?? '-'}</Typography.Text>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}
          <Divider />
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="Модель">{latest.llm_model || "-"}</Descriptions.Item>
            <Descriptions.Item label="RAG уверенность">{latest.rag_confidence ? `${(latest.rag_confidence * 100).toFixed(0)}%` : "-"}</Descriptions.Item>
            <Descriptions.Item label="Отредактирован">{latest.was_edited ? "Да" : "Нет"}</Descriptions.Item>
            <Descriptions.Item label="Модератор">{latest.moderated_by || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>
      ) : (
        <Card title={<Typography.Text strong>Ответ</Typography.Text>}>
          <Typography.Text type="secondary">
            Ответ еще не сгенерирован. Используйте настройки генерации выше, чтобы создать ответ.
          </Typography.Text>
        </Card>
      )}
    </Space>
  );
}
